/**
 * Custom date picker using Radix Popover + date-fns.
 *
 * Replaces the native <input type="date"> to fix:
 * - Calendar icon not adapting to dark mode
 * - Clear button overflowing the container
 * - Month navigation closing the picker (browser bug)
 */

import { useState, useCallback, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Calendar, X, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  isValid,
  parseISO,
} from 'date-fns';
import clsx from 'clsx';

interface DatePickerProps {
  /** ISO date string (YYYY-MM-DD) or empty string */
  value: string;
  onChange: (value: string) => void;
  /** Optional time string (HH:MM, 24-hour). When set, shows time alongside date. */
  timeValue?: string;
  /** Called when user sets or clears the time. Pass "" to clear. */
  onTimeChange?: (time: string) => void;
  disabled?: boolean;
  /** Visual variant based on due-date state */
  variant?: 'default' | 'overdue' | 'today';
}

const DAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/**
 * Format a 24-hour HH:MM time string for display using the user's locale (12-hour by default).
 * e.g. "17:00" → "5:00 PM"
 */
function formatDisplayTime(time: string): string {
  const [hourStr, minuteStr] = time.split(':');
  const hour = parseInt(hourStr ?? '0', 10);
  const minute = parseInt(minuteStr ?? '0', 10);
  const d = new Date(2000, 0, 1, hour, minute);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

export function DatePicker({ value, onChange, timeValue, onTimeChange, disabled, variant = 'default' }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const parsedDate = value ? parseISO(value) : null;
  const selectedDate = parsedDate !== null && isValid(parsedDate) ? parsedDate : null;
  const [viewMonth, setViewMonth] = useState(() => selectedDate ?? new Date());

  // Sync the calendar view month when the selected task changes (value prop changes)
  useEffect(() => {
    if (selectedDate) {
      setViewMonth(selectedDate);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = useCallback((day: Date) => {
    onChange(format(day, 'yyyy-MM-dd'));
    // Keep popover open when time input is available so user can optionally set time
    if (!onTimeChange) {
      setOpen(false);
    }
  }, [onChange, onTimeChange]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  }, [onChange]);

  // Build calendar grid for the current view month
  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const containerClasses = clsx(
    'flex items-center rounded-lg transition-all border border-transparent',
    variant === 'overdue'
      ? 'bg-red-50 dark:bg-red-950/50 hover:bg-red-100 dark:hover:bg-red-900/50'
      : variant === 'today'
      ? 'bg-primary-50/50 dark:bg-primary-950/30 hover:bg-primary-100 dark:hover:bg-primary-900/50'
      : 'bg-surface-base-light dark:bg-surface-raised-dark/50 hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark hover:border-border-hover-light dark:hover:border-border-hover-dark',
  );

  const textClasses = clsx(
    'text-sm font-medium',
    variant === 'overdue'
      ? 'text-red-700 dark:text-red-400'
      : variant === 'today'
      ? 'text-primary-700 dark:text-primary-300'
      : 'text-text-primary-light dark:text-text-primary-dark',
  );

  return (
    <Popover.Root open={open} onOpenChange={disabled ? undefined : setOpen}>
      {/* Outer div groups trigger + clear button without nesting <button> inside <button> */}
      <div className={clsx(containerClasses, 'w-full')}>
        <Popover.Trigger asChild disabled={disabled}>
          <button
            type="button"
            className={clsx(
              'flex-1 py-[11px] px-[13px] text-left cursor-pointer',
              'focus:ring-2 focus:ring-primary-500 focus:ring-inset rounded-lg',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
            disabled={disabled}
          >
            <span className={clsx('text-sm font-medium', textClasses)}>
              {selectedDate
                ? timeValue
                  ? `${format(selectedDate, 'MMM d, yyyy')} ${formatDisplayTime(timeValue)}`
                  : format(selectedDate, 'MMM d, yyyy')
                : 'No date'}
            </span>
          </button>
        </Popover.Trigger>

        {/* Clear button is a sibling of the trigger, not nested inside it */}
        {value && !disabled ? (
          <button
            type="button"
            onClick={handleClear}
            className="p-2 mr-1 text-text-tertiary-light dark:text-text-tertiary-dark hover:text-red-600 dark:hover:text-red-400 rounded transition-colors cursor-pointer"
            aria-label="Clear date"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <span className="p-2 mr-1 text-text-tertiary-light dark:text-text-tertiary-dark" aria-hidden>
            <Calendar className="w-4 h-4" />
          </span>
        )}
      </div>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className={clsx(
            'z-50 w-[280px] rounded-xl p-3',
            'bg-surface-raised-light dark:bg-surface-raised-dark',
            'border border-border-subtle-light dark:border-border-subtle-dark',
            'shadow-lg',
            'animate-in fade-in-0 zoom-in-95',
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Month navigation header */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setViewMonth((m) => subMonths(m, 1))}
              className="p-2.5 md:p-1.5 rounded-md text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors cursor-pointer"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-text-primary-light dark:text-text-primary-dark">
              {format(viewMonth, 'MMMM yyyy')}
            </span>
            <button
              type="button"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              className="p-2.5 md:p-1.5 rounded-md text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors cursor-pointer"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAY_HEADERS.map((d) => (
              <div key={d} className="text-center text-xs md:text-[11px] font-medium text-text-tertiary-light dark:text-text-tertiary-dark py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const inMonth = isSameMonth(day, viewMonth);
              const selected = selectedDate && isSameDay(day, selectedDate);
              const today = isToday(day);

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => handleSelect(day)}
                  className={clsx(
                    'h-8 w-full rounded-md text-xs font-medium transition-colors cursor-pointer',
                    !inMonth && 'text-text-tertiary-light/40 dark:text-text-tertiary-dark/40',
                    inMonth && !selected && 'text-text-primary-light dark:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark',
                    selected && 'bg-primary-500 text-surface-base-dark',
                    today && !selected && 'ring-1 ring-inset ring-primary-400 dark:ring-primary-600',
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>

          {/* Today shortcut */}
          <div className="mt-2 pt-2 border-t border-border-subtle-light dark:border-border-subtle-dark">
            <button
              type="button"
              onClick={() => handleSelect(new Date())}
              className="w-full text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 py-1 rounded transition-colors cursor-pointer"
            >
              Today
            </button>
          </div>

          {/* Time input — only shown when a date is selected and onTimeChange is provided */}
          {onTimeChange && selectedDate && (
            <div className="mt-2 pt-2 border-t border-border-subtle-light dark:border-border-subtle-dark">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-text-tertiary-light dark:text-text-tertiary-dark shrink-0">
                  Time
                </label>
                <input
                  type="time"
                  value={timeValue ?? ''}
                  onChange={(e) => onTimeChange(e.target.value)}
                  className="flex-1 text-xs rounded-md px-2 py-1 bg-surface-base-light dark:bg-surface-raised-dark/50 border border-border-subtle-light dark:border-border-subtle-dark text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                {timeValue && (
                  <button
                    type="button"
                    onClick={() => onTimeChange('')}
                    className="text-text-tertiary-light dark:text-text-tertiary-dark hover:text-red-500 transition-colors"
                    aria-label="Clear time"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
